import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from "@ngrx/signals";
import { LoginService, CachedLoginData } from "../services/login.service";
import { PreferenceService } from "../services/preference.service";
import { S25Util } from "../util/s25-util";
import { AuthService } from "../services/auth.service";
import { ModalService } from "../modules/modal/modal.service";
import { PasswordResetService } from "../services/password.reset.service";
import { LoginExemptRoute } from "../services/login.exempt.route";
import { S25Const } from "../util/s25-const";
import { Proto } from "../pojo/Proto";
import URLString = Proto.URLString;
import { s25Resource } from "../util/s25.resource";
import { UserprefService } from "../services/userpref.service";

/**
 * Picks where SSO should return the user. `initHref` is the URL the tab was opened with, which
 * preserves a deep link across bootstrap, but a consumed password-reset link is a dead end to come
 * back to, so fall back to where the user is now, then to the bare app path.
 */
export function resolveSSOReturnHref(entryHref: string, currentHref: string, appPath: string): string {
    if (!!entryHref && !PasswordResetService.isPasswordResetRoute(entryHref)) return entryHref;
    if (!PasswordResetService.isPasswordResetRoute(currentHref)) return currentHref;
    return appPath;
}

type LoginStoreUser = {
    userId: number;
    userName: string;
    contactName: string;
    securityGroupId: number;
    securityGroupName: string;
};

type State = {
    init: boolean;
    loggingIn: boolean;
    loggedIn: boolean;
    loginUrl: string | undefined;
    loginRequired: boolean | undefined;
    adminMailLink: string | undefined;
    challenge: string | undefined;
    logoutUrl: URLString | undefined;
    errorUrl: URLString | undefined;
    user: LoginStoreUser | undefined;
    ssoAccessDenied: boolean;
    _firstLoginAfterSSO: boolean;
};

export const LoginStore = signalStore(
    { providedIn: "root" },
    withState<State>({
        init: false,
        loggingIn: false,
        loggedIn: false,
        loginUrl: undefined,
        loginRequired: undefined,
        adminMailLink: undefined,
        challenge: undefined,
        logoutUrl: undefined,
        errorUrl: undefined,
        user: undefined,
        ssoAccessDenied: false,
        _firstLoginAfterSSO: false,
    }),
    withProps(() => ({
        _sessionIdResource: s25Resource({
            lazy: true,
            loader: () => UserprefService.getSessionId(),
        }),
    })),
    withComputed((store) => ({
        isMasquerading: () => {
            store.user(); // Check cookie any time user changes
            return !!S25Util.getCookie(`S25Masquerade_${window.ProData.instanceId}`);
        },
        sessionId: () => store._sessionIdResource.value(),
    })),
    withMethods((store) => {
        async function login() {
            patchState(store, { loggingIn: true });
            const instanceCookie = `S25Masquerade_${window.ProData.instanceId}`;

            if (S25Util.getCookie(instanceCookie)) {
                S25Util.deleteCookie(instanceCookie);
            }

            const [isLoggedIn, err] = await S25Util.Maybe(LoginService.getLoggedIn());
            if (isLoggedIn) {
                await AuthService.authenticate();
                patchState(store, { loggingIn: false, ssoAccessDenied: false });
                return;
            }

            const loginUrl = store.loginUrl();
            if (!!loginUrl) {
                const redirectToSSO = () => {
                    const returnHref = resolveSSOReturnHref(
                        window.ProData.initHref,
                        window.location.href,
                        window.location.origin + window.location.pathname,
                    );
                    const redirectUrl = `${loginUrl}${loginUrl.includes("?") ? "&" : "?"}redirect=${encodeURIComponent(returnHref)}`;
                    console.log("redirect url: ", `[${redirectUrl}]`);

                    if (S25Util.contains(redirectUrl, "shibboleth")) {
                        S25Util.setCookie("S25FirstSSOLogin", true);
                    }

                    window.location = redirectUrl as string & Location;
                };
                /*
                  ANG-6026 When the user has previously tried to auth, but was denied we need to do a GET to
                  login.shibboleth without the ?redirect... so that we get the 'denied' response from 25saas.
                */
                if (S25Util.getCookie("S25FirstSSOLogin")) {
                    //Make sure we don't get stuck in this mode, every other sign in click we do a full redirect school's idp to make sure the login state is still oka
                    S25Util.deleteCookie("S25FirstSSOLogin");
                    const ssoDenied = await LoginService.isLoginDeniedBySSO();
                    if (!!ssoDenied) {
                        patchState(store, { ssoAccessDenied: true });
                        const administrator = store.adminMailLink()
                            ? `<a href="${store.adminMailLink()}">administrator</a>`
                            : "administrator";
                        const message = `You do not have access to 25Live. If you believe this is an error, please contact your ${administrator}.`;
                        await ModalService.queueModal("alert", { message });
                    } else {
                        redirectToSSO();
                    }
                } else {
                    redirectToSSO();
                }
            } else {
                await ModalService.modal("login", { closable: !store.loginRequired() });
                // If login is successful, the state service will call refresh
            }
            patchState(store, { loggingIn: false });
        }

        /**
         * True when the instance requires a login the current user has not completed.
         * Embedded views are exempt: the embedding config owns the login decision there
         * (a persistent session id, or the "Require Login For Event Creation" option
         * handled by s25-auth), so config_login_required must not raise a modal in an iframe.
         */
        function isLoginGateBlocked() {
            return !S25Util.isInIframe && !!store.loginRequired() && !store.loggedIn();
        }

        return {
            login,
            isLoginGateBlocked,
            /**
             * Re-applies the login gate after a navigation. Password reset and Stripe return
             * routes are reachable while logged out, so callers skip those states and enforce
             * the gate everywhere else.
             * A user SSO has already refused (ANG-6026) keeps their denial message instead of being
             * bounced back to the identity provider.
             */
            enforceLoginGate: async () => {
                if (!isLoginGateBlocked() || store.loggingIn() || store.ssoAccessDenied()) return;
                return login();
            },
            logout: async (stateName?: string) => {
                S25Util.deleteCookie(`S25Masquerade_${window.ProData.instanceId}`);

                await AuthService.invalidateAuth(stateName);
                patchState(store, {
                    loggedIn: false,
                    user: undefined,
                });
                if (S25Const.inApp) {
                    window.location.reload();
                }
            },

            refresh: async () => {
                const [auth, prefs] = await S25Util.all([
                    LoginService.getLoginCached(),
                    PreferenceService.getPreferences(["config_login_required", "config_feedback_email"]),
                ]);

                const loginRequired = prefs?.config_login_required?.value === "true";
                const adminMailLink = S25Util.eMailLink(prefs?.config_feedback_email?.value);
                const user = {
                    contactName: auth.contactName,
                    userName: auth.userName,
                    userId: Number(auth.userId),
                    securityGroupName: auth.securityGroupName,
                    securityGroupId: Number(auth.securityGroupId),
                };

                // ANG-6808: Use the firstLoginAfterSSO value detected synchronously in onInit
                // This ensures we detect SSO login even if cache was already populated before refresh() runs
                const firstLoginAfterSSO = auth.isLoggedIn && store._firstLoginAfterSSO();

                patchState(store, {
                    init: true,
                    loginRequired,
                    adminMailLink,
                    loggedIn: !!auth.isLoggedIn,
                    loginUrl: auth.loginUrl,
                    challenge: auth.challenge,
                    logoutUrl: auth.logoutUrl,
                    errorUrl: auth.errorUrl,
                    user: !!auth.isLoggedIn ? user : undefined,
                    //a completed login clears the denial, so a later local sign-in is gated normally again
                    ssoAccessDenied: !!auth.isLoggedIn ? false : store.ssoAccessDenied(),
                    _firstLoginAfterSSO: firstLoginAfterSSO,
                });

                if (isLoginGateBlocked()) {
                    // Check current URL, not initial load URL, so navigation away from
                    // password reset or Stripe return re-enables the login check
                    if (LoginExemptRoute.isLoginExemptRoute(window.location.href)) return;
                    return login();
                }
            },
            /**
             * Gets and clears firstLoginAfterSSO flag in one atomic operation.
             * This ensures the flag is only true once per login session.
             * Used to show welcome message after SSO login.
             */
            getAndClearFirstLoginAfterSSO: () => {
                const value = store._firstLoginAfterSSO();
                if (value) {
                    patchState(store, { _firstLoginAfterSSO: false });
                }
                return value;
            },
        };
    }),
    withHooks({
        onInit: async (store) => {
            if (window.unitTesting) return;
            // ANG-6452 embedding handles its own auth, we don't want to prematurely prompty the user to login
            // For SSO CORS is easier when the user takes an action to redirect
            // For Native Auth and ldap each embeded view handles auth in its own way to avoid a modal in an iframe that might not display well
            if (S25Util.isInIframe) return;

            // ANG-6808: Detect firstLoginAfterSSO synchronously before any async calls
            // Check document.referrer immediately when store initializes, before getLoginCached() is called
            // This ensures we detect SSO login even if cache is already populated
            const SSORegex = /\/\?*saml|\/\?*sso|shib|\/\?*idp|jsession|okta|adfs|login/i;
            const isSSOReferrer = !!document.referrer && !!document.referrer.match(SSORegex);
            if (isSSOReferrer || !!S25Util.getCookie("S25FirstSSOLogin")) {
                //ANG-6957 schools are more frequently not using the type of idp in their idp domain, also check firstSSOLogin cookie
                patchState(store, { _firstLoginAfterSSO: true });
                S25Util.deleteCookie("S25FirstSSOLogin");
            }

            await store.refresh();
        },
    }),
);

export type LoginStore = InstanceType<typeof LoginStore>;
